import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Inbox, Mail, Phone, ArrowRight, Briefcase, TreePine, Home, RefreshCw,
  CheckCircle2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";
import { ArchiveOpportunityButton } from "@/components/ArchiveOpportunityButton";
import { errMsg, errCode } from "@/lib/utils";

const ASSET_ICON = { business: Briefcase, land: TreePine, multifamily: Home };
const ASSET_LABEL = { business: "Business", land: "Land", multifamily: "Multifamily" };

function scoreColor(s) {
  if (s == null) return "text-slate-500 border-white/10 bg-white/[0.02]";
  if (s >= 60) return "text-emerald-300 border-emerald-500/40 bg-emerald-500/10";
  if (s >= 30) return "text-amber-300 border-amber-500/40 bg-amber-500/10";
  return "text-slate-400 border-white/10 bg-white/[0.03]";
}

export default function SubmissionsInbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending"); // BLOCK 16: default is Pending Review

  const load = () => {
    setLoading(true);
    api.get("/listings/submissions?limit=500")
      .then((s) => {
        setItems(s.data?.items || []);
      })
      .catch(() => toast.error("Failed to load submissions"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // BLOCK 173 ,  refresh the list after an Archive/Unarchive action on a row.
  useEffect(() => {
    const handler = () => load();
    window.addEventListener("acq:inbox-refresh-request", handler);
    return () => window.removeEventListener("acq:inbox-refresh-request", handler);
  }, []);

  // BLOCK 16: emit a lightweight event whenever the list mutates so the
  // sidebar badge refreshes without waiting for its 30s poll.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("acq:pending-submissions-changed"));
  }, [items]);

  const statusOf = (it) => (it.opportunity || {}).submission_status || null;

  const filtered = useMemo(() => {
    if (tab === "pending") return items.filter((i) => statusOf(i) === "pending_review");
    // History = anything that isn't pending_review (approved / rejected / legacy no-status)
    return items.filter((i) => statusOf(i) !== "pending_review");
  }, [items, tab]);

  const counts = useMemo(() => {
    const pending = items.filter((i) => statusOf(i) === "pending_review");
    const history = items.filter((i) => statusOf(i) !== "pending_review");
    return {
      pending: pending.length,
      history: history.length,
      approved: items.filter((i) => statusOf(i) === "approved").length,
      rejected: items.filter((i) => statusOf(i) === "rejected").length,
    };
  }, [items]);

  // Refresh the whole list after a decision so pending tab, history tab,
  // and stat counters all resync from source of truth. Bugfix over pure
  // optimistic removal ,  an approved row must still appear in History.
  const onDecided = (_oid) => {
    load();
  };

  return (
    <div className="space-y-6" data-testid="submissions-inbox-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Admin · Moderation Queue</div>
          <h1 className="font-heading text-4xl tracking-tight" data-testid="inbox-page-heading">Pending Review</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Every opportunity awaiting admin approval, from any intake source ,  broker portal, manual entry, CSV import, or webhook.
            Approved rows follow their assigned visibility (private to owner, or published to the shared discovery pool).
          </p>
        </div>
        <button
          onClick={load}
          data-testid="inbox-refresh"
          className="btn-ghost text-xs inline-flex items-center gap-2"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Pending review" v={counts.pending} accent="text-amber-300" testid="inbox-stat-pending" icon={Inbox} />
        <Stat label="Approved" v={counts.approved} accent="text-emerald-300" testid="inbox-stat-approved" icon={CheckCircle2} />
        <Stat label="Rejected" v={counts.rejected} accent="text-red-300" testid="inbox-stat-rejected" icon={AlertTriangle} />
        <Stat label="History total" v={counts.history} testid="inbox-stat-history" />
      </div>

      {/* HUD LIHTC ingest panel moved to /admin/discovery-configs
          (BLOCK 39 Item 5) ,  it wasn't a submission-review action. */}

      {/* Tabs ,  BLOCK 16: Pending Review default, History separate */}
      <div className="flex items-center gap-2 border-b border-white/10 -mb-px">
        <TabBtn active={tab === "pending"} onClick={() => setTab("pending")} testid="inbox-tab-pending" pillTone="amber" count={counts.pending}>
          Pending Review
        </TabBtn>
        <TabBtn active={tab === "history"} onClick={() => setTab("history")} testid="inbox-tab-history" count={counts.history}>
          History
        </TabBtn>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="inbox-loading">Loading...</div>
      ) : filtered.length === 0 ? (
        <div className="ae-card p-12 text-center" data-testid={tab === "pending" ? "inbox-empty-pending" : "inbox-empty-history"}>
          <Inbox className="h-10 w-10 text-slate-600 mx-auto mb-3" />
          {tab === "pending" ? (
            <>
              <p className="text-sm text-slate-400">No opportunities are waiting for review.</p>
              <p className="text-xs text-slate-600 mt-1">Public broker/owner submissions and any row flagged for admin review land here.</p>
            </>
          ) : (
            <p className="text-sm text-slate-400">No approved or rejected submissions yet.</p>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="inbox-list">
          {filtered.map((it) => <SubmissionRow key={it.lsid || it.oid} it={it} onDecided={onDecided} />)}
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, testid, count, pillTone, children }) {
  const pillCls = pillTone === "amber"
    ? "bg-amber-500/15 text-amber-300 border-amber-500/40"
    : "bg-white/[0.04] text-slate-400 border-white/10";
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-xs uppercase tracking-widest transition border-b-2 inline-flex items-center gap-2 ${
        active ? "border-emerald-500 text-emerald-300" : "border-transparent text-slate-500 hover:text-slate-300"
      }`}
    >
      {children}
      {count != null && count > 0 && (
        <span className={`px-1.5 py-0.5 text-[10px] font-mono border rounded-sm ${pillCls}`} data-testid={`${testid}-count`}>
          {count}
        </span>
      )}
    </button>
  );
}

function Stat({ label, v, icon: I, accent, testid }) {
  return (
    <div className="ae-card p-4" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        {I && <I className="h-3.5 w-3.5 text-slate-500" />}
      </div>
      <div className={`font-mono tabular-nums text-2xl font-bold mt-1 ${accent || "text-slate-100"}`}>{v}</div>
    </div>
  );
}

// BLOCK 39 Item 5 ,  HudPanel moved to DiscoveryConfigsAdmin.jsx.

function SubmissionRow({ it, onDecided }) {
  const opp = it.opportunity || {};
  const Icon = ASSET_ICON[it.asset_type] || Inbox;
  const sub = it.submitter || {};
  const hasReceipt = it.has_receipt !== false; // legacy responses lack the flag → assume true
  const score = it.cf_readiness_score;
  const cls = scoreColor(score);
  const sf = opp.seller_financing_flag;
  return (
    <article className="ae-card p-5" data-testid={`inbox-row-${it.lsid || it.oid}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-10 w-10 rounded-sm bg-emerald-500/12 flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4 text-emerald-400" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
            <span>{ASSET_LABEL[it.asset_type] || it.asset_type}</span>
            <span className="opacity-40">·</span>
            <span>{opp.city || ""}{opp.city && opp.state && ", "}{opp.state || ""}</span>
            {sf && (
              <>
                <span className="opacity-40">·</span>
                <span className="text-emerald-400">Seller financing</span>
              </>
            )}
            {!hasReceipt && (
              <>
                <span className="opacity-40">·</span>
                <span className="text-slate-500 normal-case tracking-normal italic" data-testid={`inbox-legacy-flag-${it.oid}`}>
                  Legacy / non-portal intake ({it.intake_source || opp.source || "unknown"})
                </span>
              </>
            )}
          </div>
          <h3 className="font-heading text-base mt-1 leading-snug flex items-center gap-2" data-testid={`inbox-row-title-${it.lsid || it.oid}`}>
            {opp.title || sub.business_name || sub.parcel_address || sub.property_address || "Untitled listing"}
            {opp.needs_address_review && (
              <span
                className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300"
                title={opp.address_review_reason || "Legacy bug signature: address was never a real street address"}
                data-testid={`inbox-address-review-flag-${it.lsid || it.oid}`}
              >
                Needs address review
              </span>
            )}
          </h3>
          {opp.asking_price > 0 && (
            <div className="text-sm text-slate-300 font-mono tabular-nums mt-1">${Number(opp.asking_price).toLocaleString()}</div>
          )}
          <div className="flex items-center gap-3 text-xs text-slate-400 mt-2 flex-wrap">
            {sub.contact_name ? (
              <>
                <span>{sub.contact_name}</span>
                <span className="opacity-40">·</span>
                <span className="text-[11px] text-slate-500">
                  {sub.contact_role
                    ? ({ owner: "Owner", broker: "Broker/Rep", other: "Other" }[sub.contact_role] || sub.contact_role)
                    : (hasReceipt ? "Submitter" : "No submitter contact on file")}
                </span>
              </>
            ) : (
              <span className="text-[11px] text-slate-500 italic">No submitter contact on file · legacy/manual entry</span>
            )}
            {sub.contact_email && (
              <a href={`mailto:${sub.contact_email}`} className="inline-flex items-center gap-1 text-slate-400 hover:text-emerald-300" data-testid={`inbox-mail-${it.lsid || it.oid}`}>
                <Mail className="h-3 w-3" /> {sub.contact_email}
              </a>
            )}
            {sub.contact_phone && (
              <span className="inline-flex items-center gap-1 text-slate-500">
                <Phone className="h-3 w-3" /> {sub.contact_phone}
              </span>
            )}
          </div>
          <div className="text-[10px] text-slate-600 mt-2 font-mono">
            Received {it.received_at ? fmtDateTime(it.received_at) : "unknown"}
          </div>
        </div>

        <div className="flex flex-col items-end gap-2 shrink-0">
          <div className={`px-3 py-1.5 rounded-sm border text-center ${cls}`} data-testid={`inbox-score-${it.lsid || it.oid}`}>
            <div className="text-[9px] uppercase tracking-widest opacity-70">CF Score</div>
            <div className="font-mono tabular-nums text-xl font-bold">{score ?? "..."}<span className="text-[10px] opacity-60">/100</span></div>
          </div>
          {opp.submission_status && (
            <StatusPill status={opp.submission_status} lsid={it.lsid || it.oid} />
          )}
          {opp.submission_status === "pending_review" && opp.oid && (
            <ModerationButtons oid={opp.oid} lsid={it.lsid || it.oid} onDecided={onDecided} />
          )}
          {opp.oid && (
            <Link
              to={`/app/opportunities/${opp.oid}`}
              data-testid={`inbox-open-${it.lsid || it.oid}`}
              className="text-xs text-emerald-400 hover:text-emerald-300 inline-flex items-center gap-1"
            >
              Open <ArrowRight className="h-3 w-3" />
            </Link>
          )}
          {/* BLOCK 184 Part B.4 ,  Archive is now available on every
              card, pending included (was previously hidden for pending
              rows, the direct cause of an archived-while-pending
              listing staying visible with no way to re-archive it from
              here). Uses the same shared component + reason prompt as
              the opportunity detail page. */}
          {opp.oid && (
            <ArchiveOpportunityButton
              oid={opp.oid}
              archived={opp.admin_archived || opp.withdrawn}
              onChanged={() => window.dispatchEvent(new CustomEvent("acq:inbox-refresh-request"))}
            />
          )}
        </div>
      </div>
    </article>
  );
}

function StatusPill({ status, lsid }) {
  const styles = {
    pending_review: "text-amber-300 border-amber-500/40 bg-amber-500/10",
    approved: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    rejected: "text-red-300 border-red-500/40 bg-red-500/10",
  };
  const label = { pending_review: "Pending review", approved: "Approved", rejected: "Rejected" }[status] || status;
  return (
    <div className={`px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-widest ${styles[status] || ""}`}
         data-testid={`inbox-status-${lsid}`}>
      {label}
    </div>
  );
}

function ModerationButtons({ oid, lsid, onDecided }) {
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");
  const [shareOnApprove, setShareOnApprove] = useState(true);

  const decide = async (decision) => {
    if (decision === "reject" && !reason.trim()) {
      toast.error("Provide a rejection reason.");
      return;
    }
    const body = {
      decision,
      rejection_reason: decision === "reject" ? reason.trim() : null,
      visibility_on_approve: shareOnApprove ? "shared" : "private",
    };
    setBusy(true);
    try {
      await api.post(`/admin/listings/submissions/${oid}/decision`, body);
      toast.success(`Submission ${decision === "approve" ? "approved" : "rejected"}.`);
      // BLOCK 16 ,  call the parent's onDecided so it re-fetches the full
      // list. This keeps the sidebar badge, pending tab, AND history tab
      // + stats cards all correct ,  pure optimistic removal would leave
      // the approved row invisible until the user refreshed manually.
      onDecided?.(oid);
    } catch (e) {
      // BLOCK 184 ,  this action requires a fresh MFA step-up (10 min
      // TTL). A long review session easily outlives that window, so this
      // is the common case in real admin use, not an edge case ,  prompt
      // for the code and retry instead of just failing.
      if (errCode(e) === "mfa_step_up_required") {
        const code = window.prompt("This action needs a fresh authenticator code.\nEnter your current 6-digit code to continue:");
        if (!code) {
          toast.message("Cancelled , a fresh code is required to continue.");
          setBusy(false);
          return;
        }
        try {
          await api.post("/mfa/step-up", { code: code.trim() });
          await api.post(`/admin/listings/submissions/${oid}/decision`, body);
          toast.success(`Submission ${decision === "approve" ? "approved" : "rejected"}.`);
          onDecided?.(oid);
        } catch (e2) {
          toast.error(errMsg(e2, "Decision failed"));
        }
      } else {
        toast.error(errMsg(e, "Decision failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1" data-testid={`inbox-moderate-${lsid}`}>
      {!showReject ? (
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-slate-500 inline-flex items-center gap-1 cursor-pointer" title="Approve as shared (in the discovery pool) vs private (invisible to peers)">
            <input type="checkbox" checked={shareOnApprove} onChange={(e) => setShareOnApprove(e.target.checked)}
                    data-testid={`inbox-share-toggle-${lsid}`} />
            share
          </label>
          <button onClick={() => decide("approve")} disabled={busy}
                  data-testid={`inbox-approve-${lsid}`}
                  className="text-[11px] px-2.5 py-1 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15 disabled:opacity-50">
            Approve
          </button>
          <button onClick={() => setShowReject(true)} disabled={busy}
                  data-testid={`inbox-reject-btn-${lsid}`}
                  className="text-[11px] px-2.5 py-1 rounded-sm border border-red-500/40 text-red-300 hover:bg-red-500/15 disabled:opacity-50">
            Reject
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-end gap-1">
          <input value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason for reject"
                  data-testid={`inbox-reject-reason-${lsid}`}
                  className="text-[11px] px-2 py-1 rounded-sm border border-red-500/30 bg-black/40 w-56" />
          <div className="flex items-center gap-2">
            <button onClick={() => setShowReject(false)} className="text-[10px] text-slate-500">Cancel</button>
            <button onClick={() => decide("reject")} disabled={busy || !reason.trim()}
                    data-testid={`inbox-reject-confirm-${lsid}`}
                    className="text-[11px] px-2.5 py-1 rounded-sm border border-red-500/40 text-red-300 hover:bg-red-500/15 disabled:opacity-50">
              Confirm reject
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
